// The verdict — a SENTENCE, after the table, naming both Houses.
//
// Not a band, not a badge, and not the largest thing on the page. A 72px
// CARRIED across the top of this screen is the system declaring, and the system
// never declares (SO 14.1(k), migrations/0001_initial.sql:500). The largest type
// on the page is the tally; the verdict is a line of prose underneath it, which
// is also the only shape that can name both Houses without inventing a total.
//
// The three renderings are THREE COMPONENTS, not one component with three colour
// variants. That is deliberate and it is the safety property: a variant switch
// invites `carried ? 'success' : 'danger'`, and the moment anyone writes that,
// `null` — withheld pending a person — renders as the refusal of a motion Synod
// never refused. Three components cannot be collapsed by a ternary.

import type { JSX } from 'react';
import type { MotionItemResult, MotionScopeResult } from '@cca/decides-count';
import { refusedScopes, votesPhrase } from './view';

export function Verdict({ result }: { result: MotionItemResult }): JSX.Element {
  // `=== null` FIRST, and tested as an identity, never as `!result.carried`.
  // See MotionItemResult.carried: the key is never absent, it is null, and a
  // falsy test reads "no decision" as "lost".
  if (result.carried === null) return <VerdictWithheld />;
  if (result.carried === true) return <VerdictCarried result={result} />;
  return <VerdictNotCarried scopes={refusedScopes(result)} />;
}

/** Carried. Named as a conjunction when it was counted as one — "in both
 *  Houses" is the whole of SO 8.6 in three words. */
function VerdictCarried({ result }: { result: MotionItemResult }): JSX.Element {
  if (result.byHouses) {
    return <p className="result-verdict result-verdict-carried">Carried in both Houses.</p>;
  }
  const only = result.scopes[0];
  return (
    <p className="result-verdict result-verdict-carried">
      {only === undefined
        ? 'Carried.'
        : `Carried: ${String(only.ayes)} for and ${String(only.noes)} against, and ${String(only.required)} were needed.`}
    </p>
  );
}

/**
 * Not carried — and it says WHICH House refused it and by what margin, because
 * "not carried" on its own is the sentence that gets misremembered as "the vote
 * was close" or "the clergy blocked it". A motion can carry in one House and
 * fail in the other, and that IS the result; naming the failing House is not
 * detail, it is the finding.
 */
function VerdictNotCarried({ scopes }: { scopes: MotionScopeResult[] }): JSX.Element {
  const clauses = scopes.map(
    (scope) => `${votesPhrase(scope)}, and ${String(scope.required)} were needed`,
  );
  return (
    <p className="result-verdict result-verdict-refused">
      {clauses.length === 0 ? 'Not carried.' : `Not carried: ${clauses.join('; ')}.`}
    </p>
  );
}

/** Withheld. It repeats the card's first sentence and points back at it rather
 *  than paraphrasing the reasons a second time — one explanation, in one place,
 *  with the two numbers that disagree beside it. */
function VerdictWithheld(): JSX.Element {
  return (
    <p className="result-verdict result-verdict-withheld">
      No decision is reported for this motion. The tallies above are complete; the decision is
      withheld until the disagreement described above has been settled.
    </p>
  );
}
